import PropTypes from 'prop-types';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import { BottomSheet, PlainButton, tokens, useIsMobile } from 'components/v3';
import Glyph from '../../Forms/Submissions/Glyph';
import { sx } from '../../Forms/Submissions/sx';
import { copyText } from '../shared/clipboard';

// A driver who filed last year and hasn't sent this year's W-9 (design
// W9Panels.PersonModal, w9-plan §6.3 item 8): their name, when they
// filed, phone and email with copy buttons, "View <year> W-9" and
// "Copy W-9 link". A bottom sheet on phones, a centred dialog on desktop.

const Row = styled(PlainButton)`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 50px;
  padding: 0 14px;
  color: ${(p) => p.$color};
  font-size: 14.5px;
  font-weight: 600;
  text-align: left;

  & + & {
    border-top: 1px solid ${(p) => p.$line};
  }

  &:hover {
    background: ${(p) => p.$hover};
  }
`;

const PersonSheet = ({
  open,
  person,
  currentYear,
  url,
  onClose,
  onViewEntry,
  onToast,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  const s = sx(theme);
  const isMobile = useIsMobile();
  if (!person) return null;
  const filed = person.year;
  const contact = [
    person.phone
      ? { icon: 'phone', value: person.phone, what: 'Phone number' }
      : null,
    person.email ? { icon: 'mail', value: person.email, what: 'Email' } : null,
  ].filter(Boolean);
  const buttonStyle = { width: isMobile ? '100%' : 'auto' };
  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={person.name}
      subtitle={`Filed for ${filed} · nothing for ${currentYear} yet`}
      footer={
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column-reverse' : 'row',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <ButtonV2
            onClick={() => onViewEntry(person)}
            icon={<Glyph name="file-text" size={16} />}
            style={buttonStyle}
          >
            View {filed} W-9
          </ButtonV2>
          <ButtonV2
            primary
            onClick={() =>
              copyText(url, 'W-9 link', onToast).then(() => onClose())
            }
            icon={<Glyph name="link" size={16} />}
            style={buttonStyle}
          >
            Copy W-9 link
          </ButtonV2>
        </div>
      }
    >
      <div style={{ padding: '0 6px' }}>
        <p
          style={{
            margin: '0 0 12px',
            fontSize: 14,
            lineHeight: 1.55,
            color: t.sub,
          }}
        >
          They filed a W-9 with you for {filed}. Send them your collection link
          to get a {currentYear} W-9 on file.
        </p>
        {contact.length ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid ' + s.line2,
              borderRadius: 12,
              overflow: 'hidden',
              marginBottom: 6,
            }}
          >
            {contact.map((c) => (
              <Row
                key={c.what}
                onClick={() => copyText(c.value, c.what, onToast)}
                aria-label={`Copy ${c.what.toLowerCase()} ${c.value}`}
                $color={t.text}
                $line={s.line}
                $hover={s.hover}
              >
                <span style={{ display: 'flex', color: t.faint }}>
                  <Glyph name={c.icon} size={17} />
                </span>
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {c.value}
                </span>
                <span style={{ display: 'flex', color: t.faint }}>
                  <Glyph name="copy" size={14} />
                </span>
              </Row>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: t.faint, marginBottom: 6 }}>
            No phone or email on their {filed} W-9.
          </div>
        )}
      </div>
    </BottomSheet>
  );
};

PersonSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  person: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    year: PropTypes.number.isRequired,
    phone: PropTypes.string,
    email: PropTypes.string,
  }),
  currentYear: PropTypes.number.isRequired,
  url: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onViewEntry: PropTypes.func.isRequired,
  onToast: PropTypes.func.isRequired,
};

PersonSheet.defaultProps = { person: null };

export default PersonSheet;
